import React from "react";
import {useParams, Redirect, Prompt} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {useGroupManager} from "../../Providers/groupManager";
import TabScreen from "../../Components/TabScreen";
import OperatorSidebar from "../../Components/OperatorSidebar";
import OperatorDetail, {PRESETS} from "../../Components/OperatorDetail";
import ConfirmDialog from "../../Components/ConfirmDialog";
import {listOperators, updateOperator, revokeOperator} from "../../../Services/operatorFunctions";

const OperatorAccess = () => {
    const {id: campaignId} = useParams();
    const {t} = useTranslation();
    const {campaign, can, permissions, role} = useGroupManager();
    const [operators, setOperators] = React.useState([]);
    const [selectedId, setSelectedId] = React.useState("owner");
    const [dirty, setDirty] = React.useState(false); // rascunho não salvo no painel de detalhe
    const [pendingSelect, setPendingSelect] = React.useState(null); // operador aguardando confirmação de troca
    const [loading, setLoading] = React.useState(true);
    const queues = React.useRef({});

    const owner = {
        id: null, key: "owner", synthetic: true, role: "owner",
        full_name: t("words.you"), email: "", status: "active", permissions: PRESETS.owner,
    };

    const refetch = React.useCallback(async () => {
        setLoading(true);
        try {
            const data = await listOperators(campaignId);
            setOperators(Array.isArray(data) ? data.map((o) => ({...o, key: String(o.id)})) : []);
        } catch (e) {
            setOperators([]);
        }
        setLoading(false);
    }, [campaignId]);

    React.useEffect(() => { refetch(); }, [refetch]);

    const list = [owner, ...operators];
    const selected = list.find((o) => o.key === selectedId) || owner;

    const patch = (op, body) => {
        if (op.synthetic) return;
        const id = op.id;
        setOperators((l) => l.map((x) => (x.id === id ? {...x, ...body} : x)));
        const prev = queues.current[id] || Promise.resolve();
        queues.current[id] = prev
            .then(() => updateOperator(campaignId, id, body))
            .then((dto) => setOperators((l) => l.map((x) => (x.id === id ? {...dto, key: String(dto.id)} : x))))
            .catch(() => refetch());
    };

    const remove = async (op) => {
        if (op.synthetic) return;
        try {
            await revokeOperator(campaignId, op.id);
        } catch (e) {
            // Falha (rede/5xx): NÃO some da lista sem ter revogado — re-sincroniza com o servidor.
            refetch();
            return;
        }
        setOperators((l) => l.filter((x) => x.id !== op.id));
        setDirty(false);
        setSelectedId("owner");
    };

    // Troca de operador com guarda: se há rascunho não salvo, confirma antes de descartar.
    const requestSelect = (key) => {
        if (key === selectedId) return;
        if (dirty) { setPendingSelect(key); return; }
        setSelectedId(key);
    };
    const confirmSwitch = () => {
        setDirty(false);
        setSelectedId(pendingSelect);
        setPendingSelect(null);
    };

    // Deep-link guard: se o /me já carregou e não é owner, esta aba não é do operador.
    if (permissions && role !== "owner") {
        return <Redirect to={`/campaign/${campaignId}/manager/inbox`}/>;
    }

    return (
        <TabScreen sidebar={
            <OperatorSidebar operators={operators} owner={owner} selectedId={selectedId}
                onSelect={requestSelect} campaignId={campaignId} onInvited={refetch} loading={loading}
                canInvite={can("can_invite_operators")}/>
        }>
            <OperatorDetail operator={selected} campaign={campaign} onDirtyChange={setDirty}
                onUpdate={(body) => patch(selected, body)} onRevoke={() => remove(selected)}/>

            <ConfirmDialog open={pendingSelect !== null}
                title={t("words.discard_changes")}
                message={t("words.discard_changes_confirm")}
                confirmLabel={t("words.discard_changes")}
                onConfirm={confirmSwitch}
                onCancel={() => setPendingSelect(null)}/>

            {/* Sair da aba/rota com rascunho não salvo: bloqueia via navegação SPA. */}
            <Prompt when={dirty} message={t("words.unsaved_changes")}/>
        </TabScreen>
    );
};

export default OperatorAccess;
